import React, { useEffect, useState } from 'react';
import { useGuardedGenerate } from '../../hooks/useGuardedGenerate';
import { DocumentSendSection, DocumentSendSource } from './DocumentSendSection';
import { ActivityIndicator, Linking, ScrollView, StyleSheet, Switch, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from '../OverlayPanel';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useAppStore } from '../../store/appStore';
import { openFileUrl } from '../vendors/PackingSlipModal';
import { DocumentTemplatePicker } from './DocumentTemplatePicker';
import { DOCUMENT_ICONS, DocumentLanguage, DocumentLanguageChoice, DocumentType } from '../../utils/documentTypes';
import type { GeneratedDocument } from '../../services/api';

export interface DocumentRequest {
  type: DocumentType;
  templateId?: string;
  language?: DocumentLanguage;
  userLocale: string;
  hideCustomerPhone?: boolean;
  notes?: string;
  deliverBy?: string;
}

interface Props {
  visible: boolean;
  /** Types offered (one = no type choice). */
  types: DocumentType[];
  /** What it is made from, e.g. "Order ORD-2026-0042". */
  scopeLabel: string;
  /** Who "Send" goes to (customer for order documents, vendor for purchase orders). */
  recipient?: { name?: string; phone?: string; email?: string };
  /** Disabled types with the reason shown (e.g. a receipt for an unpaid order). */
  unavailable?: Partial<Record<DocumentType, string>>;
  /** Purchase orders: requested delivery date + note to the vendor. */
  purchaseOrderFields?: boolean;
  onGenerate: (request: DocumentRequest) => Promise<GeneratedDocument>;
  onClose: () => void;
  /** What the document is about — enables "Send on WhatsApp" with a message template. */
  sendSource?: DocumentSendSource;
}

/** Pick a document (type · template · language), create the PDF, then open it or send its link. */
export const DocumentModal: React.FC<Props> = ({ visible, types, scopeLabel, recipient, unavailable = {}, purchaseOrderFields, onGenerate, onClose, sendSource }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const businessId = useAppStore(s => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const [type, setType] = useState<DocumentType>(types[0]);
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [language, setLanguage] = useState<DocumentLanguageChoice>('template');
  const [templatesReady, setTemplatesReady] = useState(false);
  const [hidePhone, setHidePhone] = useState(false);
  const [notes, setNotes] = useState('');
  const [deliverBy, setDeliverBy] = useState('');
  const { busy, error, setError, result, setResult, run } = useGuardedGenerate<GeneratedDocument>([visible, scopeLabel, businessId], t('documents.failed'));

  useEffect(() => {
    if (!visible) return;
    setType(types.find((x) => !unavailable[x]) || types[0]);
    setTemplateId(undefined);
    setLanguage('template');
    setHidePhone(false);
    setNotes('');
    setDeliverBy('');
    setTemplatesReady(false);
  }, [visible, scopeLabel, businessId]);

  useEffect(() => { setTemplateId(undefined); }, [type]);

  const generate = () => {
    if (!templatesReady || unavailable[type]) return;
    if (purchaseOrderFields && deliverBy && (!/^\d{4}-\d{2}-\d{2}$/.test(deliverBy) || Number.isNaN(Date.parse(deliverBy)) || new Date(deliverBy).toISOString().slice(0, 10) !== deliverBy)) {
      setError(t('documents.picker.invalidDate'));
      return;
    }
    return run(() => onGenerate({
      type, templateId, ...(language === 'template' ? {} : { language }), userLocale: locale, hideCustomerPhone: hidePhone || undefined,
      ...(purchaseOrderFields ? { notes: notes.trim() || undefined, deliverBy: /^\d{4}-\d{2}-\d{2}$/.test(deliverBy) ? deliverBy : undefined } : {}),
    }));
  };

  const title = t(`documents.type.${type}` as any);
  const message = result ? t('documents.shareText', { name: recipient?.name || '', title, number: result.documentNumber, url: result.url }) : '';
  const phone = String(recipient?.phone || '').replace(/[^\d]/g, '');

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={[styles.backdrop, { paddingTop: Math.max(spacing.lg, insets.top), paddingBottom: Math.max(spacing.lg, insets.bottom) }]}>
        <View style={[styles.card, { maxHeight: height - Math.max(spacing.lg, insets.top) - Math.max(spacing.lg, insets.bottom) }]}>
          <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name={DOCUMENT_ICONS[type] as any} size={20} color={colors.primary as string} />
            <Text style={[styles.title, { textAlign: rtl.textStart }]}>{types.length > 1 ? t('documents.create') : title}</Text>
            <TouchableOpacity onPress={onClose} accessibilityLabel={t('common.close')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color={colors.text.secondary as string} />
            </TouchableOpacity>
          </View>
          <Text style={[styles.scope, { textAlign: rtl.textStart }]}>{scopeLabel}</Text>

          {!result ? (
            <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ gap: spacing.sm, paddingBottom: spacing.sm }} keyboardShouldPersistTaps="handled">
              {types.length > 1 && (
                <View style={styles.types}>
                  {types.map((x) => {
                    const reason = unavailable[x];
                    const active = type === x;
                    return (
                      <TouchableOpacity key={x} disabled={!!reason || busy} accessibilityRole="radio" accessibilityState={{ checked: active, disabled: !!reason || busy }} style={[styles.typeRow, { flexDirection: rtl.rowDirection }, active && styles.typeRowActive, !!reason && styles.disabled]} onPress={() => { if (active) return; setTemplatesReady(false); setTemplateId(undefined); setType(x); setError(null); }}>
                        <Ionicons name={DOCUMENT_ICONS[x] as any} size={18} color={(active ? colors.primary : colors.text.secondary) as string} />
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.typeText, { textAlign: rtl.textStart }, active && styles.typeTextActive]}>{t(`documents.type.${x}` as any)}</Text>
                          <Text style={[styles.typeHint, { textAlign: rtl.textStart }]}>{reason || t(`documents.typeHint.${x}` as any)}</Text>
                        </View>
                        {active && <Ionicons name="checkmark-circle" size={18} color={colors.primary as string} />}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
              <DocumentTemplatePicker key={`${scopeLabel}:${type}:${visible}`} type={type} templateId={templateId} onTemplate={setTemplateId} language={language} onLanguage={setLanguage} onReadyChange={setTemplatesReady} disabled={busy} />
              {purchaseOrderFields ? (
                <>
                  <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('documents.deliverBy')}</Text>
                  <TextInput editable={!busy} style={[styles.input, { textAlign: rtl.textStart }]} value={deliverBy} onChangeText={setDeliverBy} placeholder="YYYY-MM-DD" placeholderTextColor={colors.text.disabled as string} />
                  <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('documents.noteToVendor')}</Text>
                  <TextInput editable={!busy} style={[styles.input, styles.multiline, { textAlign: rtl.textStart }]} value={notes} onChangeText={setNotes} multiline placeholderTextColor={colors.text.disabled as string} />
                </>
              ) : (
                <View style={[styles.toggleRow, { flexDirection: rtl.rowDirection }]}>
                  <Text style={[styles.toggleLabel, { textAlign: rtl.textStart }]}>{t('documents.hidePhone')}</Text>
                  <Switch disabled={busy} value={hidePhone} onValueChange={setHidePhone} />
                </View>
              )}
              {!!error && <Text style={[styles.error, { textAlign: rtl.textStart }]}>{error}</Text>}
            </ScrollView>
          ) : (
            <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ gap: spacing.sm }}>
              <View style={[styles.done, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="checkmark-circle" size={22} color={colors.success as string} />
                <Text style={[styles.doneText, { textAlign: rtl.textStart }]}>{t('documents.ready', { title, number: result.documentNumber })}</Text>
              </View>
              <TouchableOpacity style={[styles.action, styles.actionPrimary, { flexDirection: rtl.rowDirection }]} onPress={() => openFileUrl(result.url)}>
                <Ionicons name="open-outline" size={18} color={colors.white as string} />
                <Text style={[styles.actionText, styles.actionTextPrimary]}>{t('documents.open')}</Text>
              </TouchableOpacity>
              {!!sendSource && (
                <DocumentSendSection
                  type={type}
                  source={sendSource}
                  document={{ url: result.url, fileName: result.fileName, documentNumber: result.documentNumber, orders: (result as any).orders }}
                  recipient={{ name: recipient?.name, phone: recipient?.phone }}
                  language={language === 'template' ? undefined : language}
                  onLeave={onClose}
                />
              )}
              {!!recipient?.email && (
                <TouchableOpacity style={[styles.action, { flexDirection: rtl.rowDirection }]} onPress={() => Linking.openURL(`mailto:${recipient.email}?subject=${encodeURIComponent(`${title} ${result.documentNumber}`)}&body=${encodeURIComponent(message)}`).catch(() => {})}>
                  <Ionicons name="mail-outline" size={18} color={colors.primary as string} />
                  <Text style={styles.actionText}>{t('documents.sendEmail')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.secondary} onPress={() => setResult(null)}>
                <Text style={styles.secondaryText}>{t('documents.another')}</Text>
              </TouchableOpacity>
            </ScrollView>
          )}
          {!result && (
            <TouchableOpacity accessibilityRole="button" style={[styles.primary, (busy || !templatesReady || !!unavailable[type]) && styles.disabled]} disabled={busy || !templatesReady || !!unavailable[type]} onPress={generate}>
              {busy ? <ActivityIndicator color={colors.white as string} /> : <Text style={styles.primaryText}>{t('documents.createPdf')}</Text>}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  backdrop: { flex: 1, ...overlayBackdropStyle(), justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  card: { width: '100%', maxWidth: 480, backgroundColor: colors.background.surface, borderRadius: spacing.borderRadius.lg, padding: spacing.lg, gap: spacing.sm, ...overlayCardLook() },
  header: { alignItems: 'center', gap: spacing.sm },
  title: { flex: 1, fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.text.primary },
  scope: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  types: { gap: spacing.xs },
  typeRow: { alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border },
  typeRowActive: { borderColor: colors.primary, backgroundColor: (colors.primary as any) + '10' },
  typeText: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600' },
  typeTextActive: { color: colors.primary },
  typeHint: { fontSize: 11, color: colors.text.secondary, marginTop: 1 },
  label: { fontSize: typography.fontSize.xs, fontWeight: '700', color: colors.text.secondary },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: spacing.borderRadius.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, fontSize: typography.fontSize.sm, color: colors.text.primary },
  multiline: { minHeight: 64, textAlignVertical: 'top' },
  toggleRow: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.md },
  toggleLabel: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600' },
  error: { color: colors.error, fontSize: typography.fontSize.sm },
  primary: { marginTop: spacing.xs, backgroundColor: colors.primary, borderRadius: spacing.borderRadius.md, paddingVertical: spacing.md, alignItems: 'center' },
  primaryText: { color: colors.white, fontWeight: '700', fontSize: typography.fontSize.base },
  disabled: { opacity: 0.5 },
  done: { alignItems: 'center', gap: spacing.sm },
  doneText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  action: { alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingVertical: spacing.sm + 2, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.primary },
  actionPrimary: { backgroundColor: colors.primary },
  actionText: { color: colors.primary, fontWeight: '700', fontSize: typography.fontSize.sm },
  actionTextPrimary: { color: colors.white },
  secondary: { alignItems: 'center', paddingVertical: spacing.sm },
  secondaryText: { color: colors.text.secondary, fontWeight: '600' },
});
